/**
 * Redis stream ids, and the one question the chat asks of them: "have I already
 * applied this event?"
 *
 * The session stream's `eventId`s are Redis stream ids — `<ms>-<seq>`, two unsigned
 * 64-bit integers. Comparing them as strings is wrong (`"9-0"` sorts after `"10-0"`)
 * and comparing them as JS numbers is only safe by luck, so each half is compared
 * as a digit string: no leading zeros, longer is bigger, same length compares
 * lexicographically. Exact for any 64-bit value, no BigInt needed.
 */

type StreamId = { ms: string; seq: string };

const DIGITS = /^\d+$/;

const stripZeros = (digits: string): string => digits.replace(/^0+(?=\d)/, "");

/** `"1700000000000-3"` -> its two halves. A bare `"1700000000000"` means seq 0. */
export const parseStreamId = (id: string): StreamId | null => {
  const [ms, seq = "0", ...rest] = id.split("-");
  if (rest.length || !ms || !DIGITS.test(ms) || !DIGITS.test(seq)) return null;
  return { ms: stripZeros(ms), seq: stripZeros(seq) };
};

const compareDigits = (a: string, b: string): number => {
  if (a.length !== b.length) return a.length < b.length ? -1 : 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/**
 * Negative when `a` is older than `b`, zero when they are the same entry, positive
 * when newer. `null` when either is not a stream id.
 */
export const compareStreamIds = (a: string, b: string): number | null => {
  const left = parseStreamId(a);
  const right = parseStreamId(b);
  if (!left || !right) return null;
  return compareDigits(left.ms, right.ms) || compareDigits(left.seq, right.seq);
};

/**
 * Should an event carrying `eventId` be applied, given the newest one already applied?
 *
 * Applied when there is nothing to compare against: the first event of a stream, an
 * event with no id (the snapshot of a session that has no stream yet), or an id we
 * cannot read — dropping an event on a parse failure would be worse than applying a
 * duplicate. Otherwise only strictly newer events apply, which is what makes a
 * replayed or doubly-delivered event (graphql-ws resubscribes on its own after a
 * reconnect) a no-op instead of duplicated text.
 */
export const isNewerStreamId = (
  eventId: string | null,
  lastApplied: string | null,
): boolean => {
  if (!eventId || !lastApplied) return true;
  const order = compareStreamIds(eventId, lastApplied);
  return order === null || order > 0;
};

/** The newer of two ids — what the store remembers after applying an event. */
export const newestStreamId = (
  current: string | null,
  candidate: string | null,
): string | null => {
  if (!candidate) return current;
  if (!current) return candidate;
  const order = compareStreamIds(candidate, current);
  return order !== null && order > 0 ? candidate : current;
};
